import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ORDER } from "@/constants/testIds";

const TEMPLATES = [
  {
    label: "Question about timing",
    subject: "A quick question on timing",
    body: "Hello,\n\nWould you be able to share an estimate on when this might arrive?\n\nWith thanks,",
  },
  {
    label: "Update on sizing",
    subject: "Sizing update",
    body: "Hello,\n\nA quick update — please use the size below instead of what I originally noted on the order.\n\nWith thanks,",
  },
  {
    label: "Cancel the order",
    subject: "Please cancel this order",
    body: "Hello,\n\nApologies — would it be possible to cancel this order? Happy to discuss in person at the shop.\n\nWith thanks,",
  },
];

/**
 * MemberEmailComposer — lets a member fire a one-off email to the pro shop
 * about this order. Mirrors the admin EmailComposer but reverses direction.
 */
export default function MemberEmailComposer({ order, onSent }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [logAsMessage, setLogAsMessage] = useState(true);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setBody(t.body);
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setFeedback(null);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/member-email`, {
        subject: subject.trim(),
        body: body.trim(),
        log_as_message: logAsMessage,
      });
      onSent?.(data.order);
      setSubject("");
      setBody("");
      setFeedback({
        type: data.delivered ? "success" : "warning",
        text: data.delivered
          ? "Sent — the pro shop has been emailed."
          : "Saved on the order. Email could not be delivered right now; the pro shop will see your note here regardless.",
      });
      setTimeout(() => setFeedback(null), 8000);
    } catch (err) {
      setFeedback({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="surface-card p-8" data-testid="member-email-composer">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">Reach The Pro Shop</div>
          <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Email About This Order</h2>
          <p className="text-sm text-[#7A7A7A] mt-1">
            Goes straight to the pro shop with this order&rsquo;s reference attached. Replies come back to{" "}
            <span className="font-mono text-[#1A422D]">{order.member_email}</span>.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="member-email-toggle"
          className="btn-secondary text-xs"
        >
          {open ? "Close" : "Compose"}
        </button>
      </div>

      {open && (
        <div className="mt-6 fade-up">
          <div className="flex flex-wrap gap-2 mb-5">
            {TEMPLATES.map((t) => (
              <button
                key={t.label}
                type="button"
                onClick={() => applyTemplate(t)}
                className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#B59A5A] text-[#8a6f33] bg-[#FBF6E8] hover:bg-[#F3ECD6] transition-colors"
              >
                {t.label}
              </button>
            ))}
          </div>

          <label className="label-elegant block mb-2">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="member-email-subject"
            className="input-field"
            placeholder="A quick note to the pro shop"
          />

          <label className="label-elegant block mt-5 mb-2">Message</label>
          <textarea
            rows={7}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="member-email-body"
            className="input-field font-serif text-base leading-relaxed"
            placeholder={"Hello,\n\n…\n\nWith thanks,"}
          />

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] cursor-pointer">
              <input
                type="checkbox"
                checked={logAsMessage}
                onChange={(e) => setLogAsMessage(e.target.checked)}
                className="accent-[#1A422D] w-4 h-4"
              />
              Also log this in the order&rsquo;s message thread
            </label>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !subject.trim() || !body.trim()}
              data-testid="member-email-send"
              className="btn-primary"
            >
              {sending ? "Sending…" : "Send to Pro Shop"}
            </button>
          </div>

          {feedback && (
            <div
              className={`mt-5 px-4 py-3 text-sm border ${
                feedback.type === "success"
                  ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                  : feedback.type === "warning"
                    ? "border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33]"
                    : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
              }`}
            >
              {feedback.text}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
